/**
 * LP Section Preview Renderers — MA-LP-LAYOUT-027 + MA-LP-LAYOUT-027B
 * ────────────────────────────────────────────────────────────────
 * RENDER TARGET: In-canvas page preview (aligned to served LP)
 *
 * This module mirrors the backend renderer (lp_section_renderers.py)
 * section-by-section. Both render targets follow the same presentation
 * contract: structure, spacing, hierarchy, CTA treatment, layout.
 *
 * MA-LP-LAYOUT-027B: Token source is now DYNAMIC — resolved from
 * the tenant's brand config via resolveLPBrandTokens(). This ensures
 * the Builder canvas matches the served LP exactly.
 *
 * Preview logic must NOT drift independently from served LP behavior.
 * When the backend renderer changes, this file must be updated to match.
 * ────────────────────────────────────────────────────────────────
 */

import { ChevronDown } from "lucide-react";
import { useState, lazy, Suspense } from "react";
import { resolveLPBrandTokens, getSurfaceConfig as _getSurfaceConfig } from "@/lib/resolveLPBrandTokens";
import { DEFAULT_LP_THEME } from "@/lib/resolveThemeSource";
import { themeToLpBridgeTokens } from "@/lib/themeEngine";

const ConversationFunnel = lazy(() => import("@/components/ConversationFunnel"));

// ──────────────────────────────────────────────────────────────────
// Dynamic Brand Token System — MA-LP-LAYOUT-027B + MA-LP-THEME-036
// MA-THEME-RENDER-STEP2: Accepts either canonical theme or legacy config.
// When canonical theme is provided, tokens come from the bridge (single source of truth).
// ──────────────────────────────────────────────────────────────────
let _T = resolveLPBrandTokens(DEFAULT_LP_THEME);

/**
 * MA-THEME-RENDER-STEP2: Set active brand tokens.
 * Accepts either:
 * - Canonical theme object (has .tokens + .mode) → uses bridge tokens
 * - Legacy config { accent, surface_mode } → uses resolveLPBrandTokens()
 */
export function setActiveBrand(config) {
  if (config?.tokens && config?.mode) {
    // Canonical theme path — bridge tokens (same source as runtime)
    _T = themeToLpBridgeTokens(config);
  } else {
    // Legacy path
    const theme = {
      accent: config?.accent || DEFAULT_LP_THEME.accent,
      surface_mode: config?.surface_mode || DEFAULT_LP_THEME.surface_mode,
    };
    _T = resolveLPBrandTokens(theme);
  }
}

/** Get current active tokens (for external consumers that need read access) */
export function getActiveTokens() {
  return _T;
}

// ──────────────────────────────────────────────────────────────────
// Shared style factories — recomputed from active tokens each render
// ──────────────────────────────────────────────────────────────────
function cardBase() {
  return {
    background: _T.cardBg,
    border: _T.cardBorder,
    borderRadius: _T.radiusLg,
    padding: "24px",
    maxWidth: _T.maxW,
    margin: "0 auto 24px",
  };
}

function cardPrimary() {
  return {
    ...cardBase(),
    boxShadow: `${_T.cardShadow}, ${_T.cardInnerHL}`,
  };
}

function cardH2() {
  return {
    fontFamily: _T.fontHeading,
    fontSize: "clamp(1.15rem, 2.5vw, 1.35rem)",
    fontWeight: 600,
    letterSpacing: "-0.02em",
    marginBottom: "16px",
    color: _T.textPrimary,
  };
}

function ctaBtn() {
  return {
    display: "inline-block",
    padding: "14px 28px",
    background: `linear-gradient(180deg, ${_T.accent} 0%, ${_T.accentStrong} 100%)`,
    color: _T.textOnAccent,
    borderRadius: _T.radiusBtn,
    fontFamily: _T.fontHeading,
    fontWeight: 600,
    fontSize: "1rem",
    letterSpacing: "0.01em",
    textDecoration: "none",
    border: "none",
    boxShadow: `0 8px 20px ${_T.accentGlow}`,
  };
}

function bulletLi() {
  return {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "8px 0",
    fontSize: "15px",
    color: _T.textSecondary,
    lineHeight: 1.6,
  };
}

// SVG icons matching backend lp_section_renderers.py exactly
const GreenCheckSVG = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: _T.success, flexShrink: 0, marginTop: 3 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const RedXSVG = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: _T.danger, flexShrink: 0, marginTop: 3 }}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// Trust signal icon library (shared with backend render_trust_signals)
const TRUST_ICONS = {
  shield: (color) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color, flexShrink: 0 }}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  check: (color) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color, flexShrink: 0 }}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  clock: (color) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color, flexShrink: 0 }}>
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  lock: (color) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color, flexShrink: 0 }}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  ),
  star: (color) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color, flexShrink: 0 }}>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  heart: (color) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color, flexShrink: 0 }}>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ),
  "thumbs-up": (color) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color, flexShrink: 0 }}>
      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
    </svg>
  ),
  "phone": (color) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color, flexShrink: 0 }}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  "award": (color) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color, flexShrink: 0 }}>
      <circle cx="12" cy="8" r="7" /><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
    </svg>
  ),
  "users": (color) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color, flexShrink: 0 }}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
};

// Export for Trust Signal icon picker in builder
export const TRUST_ICON_KEYS = Object.keys(TRUST_ICONS);

function TrustIcon({ iconKey, color }) {
  const renderer = TRUST_ICONS[iconKey] || TRUST_ICONS.shield;
  return renderer(color || _T.accent);
}

// ──────────────────────────────────────────────────────────────────
// Per-section renderers — mirrors backend lp_section_renderers.py
// ──────────────────────────────────────────────────────────────────

/** Hero — matches render_hero + .lp-hero CSS */
function PreviewHero({ data }) {
  if (!data || typeof data !== "object") data = {};
  return (
    <section
      data-section="hero"
      style={{
        background: _T.surface1 || _T.bgPage,
        color: _T.textPrimary,
        textAlign: "center",
        containerType: "inline-size",
      }}
    >
      <style>{`
        .lp-prev-hero-inner { padding:64px 24px 24px; }
        .lp-prev-hero-h1 { font-weight:800;letter-spacing:-0.03em;line-height:1.15;margin-bottom:8px;max-width:600px;margin-left:auto;margin-right:auto;font-size:2.3rem; }
        .lp-prev-hero-sub { max-width:520px;margin:0 auto 16px;line-height:1.55;font-size:1.05rem; }
        @container (max-width: 700px) {
          .lp-prev-hero-h1 { font-size:1.55rem; }
          .lp-prev-hero-sub { font-size:0.92rem; }
        }
        @container (min-width: 701px) and (max-width: 1000px) {
          .lp-prev-hero-h1 { font-size:1.85rem; }
          .lp-prev-hero-sub { font-size:0.97rem; }
        }
      `}</style>
      <div className="lp-prev-hero-inner">
        {data.headline && (
          <h1 className="lp-prev-hero-h1" style={{ fontFamily: _T.fontHeading, color: _T.textPrimary }}>
            {data.headline}
          </h1>
        )}
        {data.subheadline && (
          <p className="lp-prev-hero-sub" style={{ color: _T.textSecondary }}>
            {data.subheadline}
          </p>
        )}
        {data.cta_label && (
          <span style={{ ...ctaBtn(), marginTop: "8px" }}>{data.cta_label}</span>
        )}
        {data.reassurance_line && (
          <p style={{ fontSize: "0.8rem", color: _T.textMuted, marginTop: "16px", letterSpacing: "0.02em" }}>
            {data.reassurance_line}
          </p>
        )}
      </div>
    </section>
  );
}

/** Trust Signals — matches render_trust_signals + .lp-trust-strip CSS
 *  MA-LP-UX-043: Single icon, responsive, max 3 desktop / 2 mobile, single row
 *  Uses @container query to adapt when preview panel is narrow */
function PreviewTrustSignals({ data }) {
  const items = Array.isArray(data) ? data : [];
  if (!items.length) return null;
  const firstItem = items.find(ts => (typeof ts === "object" ? ts?.text : ts)?.trim?.());
  const sectionIcon = (typeof firstItem === "object" ? firstItem?.icon : null) || "shield";
  return (
    <div
      data-section="trust_signals"
      style={{ containerType: "inline-size", background: _T.surface2 }}
    >
      <style>{`
        .lp-prev-trust-row { display:flex;justify-content:center;align-items:center;gap:14px;flex-wrap:nowrap;padding:16px 24px 24px;overflow:hidden; }
        .lp-prev-trust-item { display:flex;align-items:center;gap:6px;font-size:13px;font-weight:600;letter-spacing:0.02em;white-space:nowrap;flex-shrink:1;min-width:0; }
        .lp-prev-trust-item svg { width:14px;height:14px;flex-shrink:0; }
        @container (max-width: 420px) {
          .lp-prev-trust-row { gap:8px;padding:12px 16px 16px; }
          .lp-prev-trust-item { font-size:10px; }
          .lp-prev-trust-item svg { width:11px;height:11px; }
        }
      `}</style>
      <div className="lp-prev-trust-row">
        {items.slice(0, 3).map((ts, i) => {
          const text = typeof ts === "string" ? ts : ts?.text || "";
          if (!text.trim()) return null;
          return (
            <div key={i} className="lp-prev-trust-item" style={{ color: _T.textSecondary }}>
              <TrustIcon iconKey={sectionIcon} color={_T.accent} />
              <span>{text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** How It Works — matches render_how_it_works + .lp-card + .lp-hiw-* CSS */
function PreviewHowItWorks({ data }) {
  if (!data || typeof data !== "object") data = {};
  const steps = data.steps || [];
  return (
    <section style={cardPrimary()} data-section="how_it_works">
      <h2 style={cardH2()}>{data.headline || "How It Works"}</h2>
      {steps.map((step, i) => (
        <div key={i} style={{ display: "flex", gap: "24px", alignItems: "flex-start", marginBottom: i < steps.length - 1 ? "24px" : 0 }}>
          <span style={{
            width: 34, height: 34, borderRadius: "100px",
            background: `linear-gradient(180deg, ${_T.accent} 0%, ${_T.accentStrong} 100%)`,
            color: _T.textOnAccent, display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: _T.fontHeading, fontWeight: 800, fontSize: "0.8rem", flexShrink: 0,
            boxShadow: `0 2px 8px ${_T.accentGlow}`,
          }}>
            {i + 1}
          </span>
          <div>
            <div style={{ fontFamily: _T.fontHeading, fontWeight: 600, color: _T.textPrimary, marginBottom: 3, fontSize: "0.95rem" }}>
              {step.title}
            </div>
            {step.description && (
              <div style={{ fontSize: "15px", color: _T.textSecondary, lineHeight: 1.6 }}>{step.description}</div>
            )}
          </div>
        </div>
      ))}
    </section>
  );
}

/** Bullet section — matches _bullet_section helper */
function PreviewBulletSection({ data, variant, dataSectionName }) {
  if (!data || typeof data !== "object") data = {};
  const bullets = data.bullets || [];
  if (!bullets.length) return null;

  const isSuccess = variant === "success";
  const sectionStyle = {
    ...cardPrimary(),
    borderLeft: `4px solid ${isSuccess ? _T.success : _T.danger}`,
    background: isSuccess ? _T.successBg : _T.dangerBg,
  };

  return (
    <section style={sectionStyle} data-section={dataSectionName}>
      <h2 style={cardH2()}>{data.headline || ""}</h2>
      <ul style={{ listStyle: "none", padding: 0 }}>
        {bullets.map((b, i) => {
          const text = typeof b === "string" ? b : b?.text || b?.title || "";
          if (!text.trim()) return null;
          return (
            <li key={i} style={bulletLi()}>
              {isSuccess ? <GreenCheckSVG /> : <RedXSVG />}
              <span>{text}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Mid CTA — matches render_mid_cta + .lp-cta-block CSS
 *  MA-LP-UX-042: supporting_text rendered below CTA button */
function PreviewMidCta({ data }) {
  if (!data || typeof data !== "object") data = {};
  return (
    <section
      data-section="mid_cta"
      style={{ textAlign: "center", padding: "24px 24px", maxWidth: _T.maxW, margin: "0 auto" }}
    >
      {data.headline && <h2 style={{ ...cardH2(), textAlign: "center" }}>{data.headline}</h2>}
      <span style={ctaBtn()}>{data.button_text || "Get Started"}</span>
      {data.urgency_text && (
        <p style={{ fontSize: "13px", color: _T.textMuted, marginTop: "10px", letterSpacing: "0.015em" }}>
          {data.urgency_text}
        </p>
      )}
      {data.supporting_text && (
        <p data-testid="mid-cta-supporting-text" style={{ fontSize: "0.82rem", color: _T.textMuted, marginTop: "10px", letterSpacing: "0.01em", maxWidth: "480px", marginLeft: "auto", marginRight: "auto" }}>
          {data.supporting_text}
        </p>
      )}
    </section>
  );
}

/** What Happens Next — matches render_what_happens_next + .lp-whn-* CSS */
function PreviewWhatHappensNext({ data }) {
  if (!data || typeof data !== "object") data = {};
  const bullets = data.bullets || [];
  if (!bullets.length) return null;

  return (
    <section style={cardPrimary()} data-section="what_happens_next">
      <h2 style={cardH2()}>{data.headline || "What Happens Next"}</h2>
      <ul style={{ listStyle: "none", padding: 0 }}>
        {bullets.map((b, i) => {
          const text = typeof b === "string" ? b : b?.text || b?.title || "";
          if (!text.trim()) return null;
          return (
            <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "24px", padding: "10px 0", fontSize: "15px", color: _T.textSecondary, lineHeight: 1.6 }}>
              {/* MA-THEME-OVERRIDE-097: Standardized with How It Works - use accent gradient, larger size, box shadow */}
              <span style={{
                width: 34, height: 34, borderRadius: "100px",
                background: `linear-gradient(180deg, ${_T.accent} 0%, ${_T.accentStrong} 100%)`,
                color: _T.textOnAccent,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                fontFamily: _T.fontHeading, fontWeight: 800, fontSize: "0.8rem", flexShrink: 0,
                boxShadow: `0 2px 8px ${_T.accentGlow}`,
              }}>
                {i + 1}
              </span>
              <span>{text}</span>
            </li>
          );
        })}
      </ul>
      {data.reassurance && (
        <p style={{ fontSize: "0.84rem", color: _T.textMuted, marginTop: "24px", fontStyle: "italic" }}>
          {data.reassurance}
        </p>
      )}
    </section>
  );
}

/**
 * What This Is / What This Is Not — COMPOSITE section.
 * MA-LP-RENDER-038 + MA-LP-RENDER-039 + MA-LP-RENDER-040
 *
 * Responsive:
 *  - Desktop (container >= 500px): Single card, CSS grid 1fr 1fr
 *  - Mobile (container < 500px): Two separate stacked cards
 *
 * MA-LP-RENDER-040: Uses cardPrimary() token system for:
 *  background, border, border-radius, shadow, padding, max-width, margin
 */
const WHAT_COMPOSITE_ID = "lp-what-composite";

function WhatIsColumn({ data, bullets, variant }) {
  const isNot = variant === "not";
  const headline = data?.headline || (isNot ? "What This Is NOT" : "What This Is");
  const Icon = isNot ? RedXSVG : GreenCheckSVG;

  return (
    <div className={`${WHAT_COMPOSITE_ID}-col`}>
      <h2 style={cardH2()}>{headline}</h2>
      <ul style={{ listStyle: "none", padding: 0 }}>
        {bullets.map((b, i) => {
          const text = typeof b === "string" ? b : b?.text || "";
          return text.trim() ? <li key={i} style={bulletLi()}><Icon /><span>{text}</span></li> : null;
        })}
      </ul>
    </div>
  );
}

function PreviewWhatComposite({ isData, notData }) {
  const wtiB = (isData && typeof isData === "object") ? (isData.bullets || []) : [];
  const wtinB = (notData && typeof notData === "object") ? (notData.bullets || []) : [];
  if (!wtiB.length && !wtinB.length) return null;

  const hasBoth = wtiB.length > 0 && wtinB.length > 0;

  // Build token-driven values (same source as cardPrimary / cardBase)
  const bg = _T.cardBg;
  const border = _T.cardBorder;         // Already full string: "1px solid rgba(...)"
  const radius = _T.radiusLg;           // e.g. "12px"
  const shadow = `${_T.cardShadow}, ${_T.cardInnerHL}`;
  const maxW = _T.maxW;

  return (
    <div data-section="what_this_is" className={WHAT_COMPOSITE_ID} style={{ containerType: "inline-size" }}>
      <style>{`
        .${WHAT_COMPOSITE_ID} .${WHAT_COMPOSITE_ID}-grid {
          display: grid;
          grid-template-columns: ${hasBoth ? "1fr 1fr" : "1fr"};
          gap: ${hasBoth ? "40px" : "0"};
          background: ${bg};
          border: ${border};
          border-radius: ${radius};
          box-shadow: ${shadow};
          padding: 24px;
          max-width: ${maxW};
          margin: 0 auto 24px;
        }
        .${WHAT_COMPOSITE_ID} .${WHAT_COMPOSITE_ID}-col {
          padding: 0;
        }
        @container (max-width: 500px) {
          .${WHAT_COMPOSITE_ID} .${WHAT_COMPOSITE_ID}-grid {
            display: flex;
            flex-direction: column;
            gap: 0;
            background: none;
            border: none;
            border-radius: 0;
            box-shadow: none;
            padding: 0;
            margin: 0 auto;
          }
          .${WHAT_COMPOSITE_ID} .${WHAT_COMPOSITE_ID}-col {
            background: ${bg};
            border: ${border};
            border-radius: ${radius};
            box-shadow: ${shadow};
            padding: 24px;
            margin-bottom: 20px;
          }
        }
      `}</style>
      <div className={`${WHAT_COMPOSITE_ID}-grid`}>
        {wtiB.length > 0 && <WhatIsColumn data={isData} bullets={wtiB} variant="is" />}
        {wtinB.length > 0 && <WhatIsColumn data={notData} bullets={wtinB} variant="not" />}
      </div>
    </div>
  );
}

/** FAQ — matches render_faq + .lp-faq-item CSS (accordion behavior) */
function PreviewFAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: _T.borderSubtle }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        style={{
          width: "100%", background: "none", border: "none", cursor: "pointer",
          padding: "16px 0", fontFamily: _T.fontHeading, fontWeight: 600,
          fontSize: "0.92rem", color: _T.textPrimary, display: "flex",
          justifyContent: "space-between", alignItems: "center", gap: "24px",
          textAlign: "left",
        }}
      >
        <span>{q}</span>
        <ChevronDown
          size={18}
          style={{
            color: _T.textMuted, flexShrink: 0,
            transform: open ? "rotate(180deg)" : "rotate(0)",
            transition: "transform 0.25s ease",
          }}
        />
      </button>
      {open && (
        <p style={{ padding: "0 0 16px", fontSize: "15px", color: _T.textSecondary, lineHeight: 1.7 }}>
          {a}
        </p>
      )}
    </div>
  );
}

function PreviewFAQ({ data }) {
  const items = Array.isArray(data) ? data : data?.items || [];
  const validItems = items.filter(
    (item) => (item.question || item.q || "").trim() && (item.answer || item.a || "").trim()
  );
  if (!validItems.length) return null;

  return (
    <section
      data-section="faq"
      style={{
        ...cardBase(),
        borderRadius: _T.radiusMd,
        padding: "24px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
      }}
    >
      <h2 style={cardH2()}>Frequently Asked Questions</h2>
      {validItems.map((item, i) => (
        <PreviewFAQItem key={i} q={item.question || item.q} a={item.answer || item.a} />
      ))}
    </section>
  );
}

/** Final CTA — matches render_final_cta + .lp-final-cta CSS */
/** Final CTA — matches render_final_cta + .lp-final-cta CSS
 *  MA-LP-UX-042: supporting_text rendered below CTA button */
function PreviewFinalCta({ data }) {
  if (!data || typeof data !== "object") data = {};
  return (
    <section
      data-section="final_cta"
      style={{
        background: _T.surface1 || _T.bgPage,
        color: _T.textPrimary, padding: "64px 24px", textAlign: "center",
      }}
    >
      <h2 style={{
        fontFamily: _T.fontHeading, color: _T.textPrimary,
        fontSize: "clamp(1.2rem, 3vw, 1.5rem)", fontWeight: 600,
        letterSpacing: "-0.02em", marginBottom: "16px",
      }}>
        {data.headline || "Ready to Get Started?"}
      </h2>
      {data.body && (
        <p style={{ color: _T.textSecondary, maxWidth: "480px", margin: "0 auto 24px", fontSize: "15px", lineHeight: 1.6 }}>
          {data.body}
        </p>
      )}
      <span style={ctaBtn()}>{data.button_text || "Get Started"}</span>
      {data.supporting_text && (
        <p data-testid="final-cta-supporting-text" style={{ fontSize: "0.82rem", color: _T.textMuted, marginTop: "10px", letterSpacing: "0.01em", maxWidth: "480px", marginLeft: "auto", marginRight: "auto" }}>
          {data.supporting_text}
        </p>
      )}
    </section>
  );
}

/** Personal Authority — MA-LP-SECTION-048
 *  Introduces the advisor, reframes assumptions, builds trust.
 *  Uses token system for all styling. */
function PreviewPersonalAuthority({ data }) {
  if (!data || typeof data !== "object") data = {};
  const title = (data.title || "").trim();
  const bodyText = (data.body_text || "").trim();
  if (!title && !bodyText) return null;

  const name = data.name || "";
  const subtitle = data.subtitle || "";
  const profileUrl = (data.profile_image_url || "").trim();
  const reassurance = (data.reassurance_text || "").trim();

  return (
    <section style={cardPrimary()} data-section="personal_authority" data-testid="lp-section-personal-authority">
      <style>{`
        .lp-prev-pa-section { container-type: inline-size; }
        .lp-prev-pa-profile { display:flex;flex-direction:column;align-items:center;text-align:center;gap:6px;margin-bottom:16px; }
        .lp-prev-pa-img { width:72px;height:72px;border-radius:50%;object-fit:cover;border:2px solid ${_T.accent}20; }
        .lp-prev-pa-placeholder {
          width:72px;height:72px;border-radius:50%;
          background:${_T.surface2};border:2px solid ${_T.accent}20;
          display:flex;align-items:center;justify-content:center;
        }
        .lp-prev-pa-placeholder svg { color:${_T.textMuted}; }
        .lp-prev-pa-name { font-family:${_T.fontHeading};font-weight:600;font-size:0.95rem;color:${_T.textPrimary};letter-spacing:-0.01em; }
        .lp-prev-pa-title { font-family:${_T.fontHeading};font-weight:700;font-size:clamp(1rem,2vw,1.15rem);color:${_T.textPrimary};letter-spacing:-0.02em; }
        .lp-prev-pa-subtitle { font-size:0.82rem;color:${_T.textMuted};letter-spacing:0.01em; }
        .lp-prev-pa-divider { height:1px;background:${_T.accent}30;margin:16px auto;max-width:80px; }
        .lp-prev-pa-body { max-width:520px;margin:0 auto;text-align:center; }
        .lp-prev-pa-body p { font-size:15px;color:${_T.textSecondary};line-height:1.65; }
        .lp-prev-pa-reassurance {
          margin-top:20px;padding:14px 20px;border-radius:${_T.radiusMd};
          background:${_T.surface2};text-align:center;max-width:480px;margin-left:auto;margin-right:auto;
        }
        .lp-prev-pa-reassurance p { font-size:0.84rem;color:${_T.textMuted};font-style:italic;margin:0; }
        @container (max-width: 500px) {
          .lp-prev-pa-img, .lp-prev-pa-placeholder { width:56px;height:56px; }
          .lp-prev-pa-body p { font-size:14px; }
          .lp-prev-pa-reassurance { padding:10px 14px; }
        }
      `}</style>
      <div className="lp-prev-pa-section">
        <div className="lp-prev-pa-profile">
          {profileUrl ? (
            <img src={profileUrl} alt={name || title} className="lp-prev-pa-img" />
          ) : (
            <div className="lp-prev-pa-placeholder">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
          )}
          {name && <div className="lp-prev-pa-name">{name}</div>}
          {title && <div className="lp-prev-pa-title">{title}</div>}
          {subtitle && <div className="lp-prev-pa-subtitle">{subtitle}</div>}
        </div>
        <div className="lp-prev-pa-divider" />
        {bodyText && (
          <div className="lp-prev-pa-body">
            <p>{bodyText}</p>
          </div>
        )}
        {reassurance && (
          <div className="lp-prev-pa-reassurance">
            <p>{reassurance}</p>
          </div>
        )}
      </div>
    </section>
  );
}

/** Funnel Embed — MA-LP-MODULAR-006A + MA-LP-LAYOUT-027 + 027B
 *  Native rendering: no card wrapping, no embed feel.
 *  Funnel is the primary interactive element.
 *  Passes LP accent tokens for theme inheritance.
 *  MA-LP-LAYOUT-027B: lpTheme now uses actual surfaceMode, not hardcoded "lp_dark".
 */
function PreviewFunnelEmbed({ data }) {
  if (!data || typeof data !== "object") data = {};
  const embedKey = (data.embed_key || "").trim();
  const hasFunnel = !!embedKey;
  const layout = data.layout || "centered";
  const height = parseInt(data.height, 10) || 280;
  // MA-LP-LAYOUT-027B: Default to true — funnels in LP always inherit LP tokens
  const adaptTheme = data.adapt_lp_theme !== false;
  // MA-LP-LAYOUT-027B: Use actual surface mode from resolved tokens
  const lpTheme = adaptTheme ? `lp_${_T.surfaceMode}` : null;

  // MA-LP-LAYOUT-027: Pass LP accent tokens to funnel for theme inheritance
  // MA-FUNNEL-PARITY-004: Pass comprehensive tokens so funnel bypasses resolveThemeTokens
  // MA-THEME-WIDGET-MIGRATION-012: Add cardBorder and borderSubtle for consent card styling
  const lpTokens = adaptTheme ? {
    accent: _T.accent,
    accentStrong: _T.accentStrong,
    accentGlow: _T.accentGlow,
    bgPage: _T.bgPage,
    surface1: _T.surface1,
    cardBg: _T.cardBg,
    cardBorder: _T.cardBorder,        // Border for cards (includes "1px solid")
    borderSubtle: _T.borderSubtle,    // Subtle border value
    textPrimary: _T.textPrimary,
    textSecondary: _T.textSecondary,
    textMuted: _T.textMuted,
    textOnAccent: _T.textOnAccent,
    accentSoft: _T.accentLight,
    textOnAccentSoft: _T.textOnAccentSoft || _T.textPrimary,
  } : null;

  return (
    <div
      id="funnel-anchor"
      data-section="funnel_embed"
      style={{
        width: "100%",
        padding: "40px 16px",
        background: _T.surface2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {(data.headline || data.subheadline) && (
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          {data.headline && (
            <h2 style={{
              fontFamily: _T.fontHeading, fontSize: "clamp(1.15rem,2.5vw,1.35rem)",
              fontWeight: 600, letterSpacing: "-0.02em", color: _T.textPrimary, marginBottom: 6,
            }}>
              {data.headline}
            </h2>
          )}
          {data.subheadline && (
            <p style={{ fontSize: "15px", color: _T.textSecondary, maxWidth: 480, margin: "0 auto" }}>
              {data.subheadline}
            </p>
          )}
        </div>
      )}
      {hasFunnel ? (
        <div style={{
          minHeight: height, overflow: "visible", position: "relative",
          background: "transparent",
          width: "100%",
          maxWidth: "640px",
        }}>
          <Suspense fallback={
            <div style={{ minHeight: height, background: _T.surface2, borderRadius: _T.radiusLg,
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <p style={{ color: _T.textMuted, fontSize: "13px" }}>Loading funnel...</p>
            </div>
          }>
            <ConversationFunnel
              key={`${embedKey}-${lpTheme || "native"}`}
              embedKey={embedKey}
              lpTheme={lpTheme}
              lpTokens={lpTokens}
              testMode={true}
              consentTextOverride={data.consent_text || null}
            />
          </Suspense>
        </div>
      ) : (
        <div style={{
          minHeight: 200, borderRadius: _T.radiusLg,
          background: _T.surface1, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <p style={{ color: _T.textMuted, fontSize: "15px" }}>No funnel selected</p>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Section Dispatcher — mirrors backend render_section()
// ──────────────────────────────────────────────────────────────────

export function renderPreviewSection(sectionKey, data, allSectionData = {}) {
  switch (sectionKey) {
    case "hero":
      return <PreviewHero data={data} />;
    case "trust_signals":
      return <PreviewTrustSignals data={data} />;
    case "how_it_works":
      return <PreviewHowItWorks data={data} />;
    case "personal_authority":
      return <PreviewPersonalAuthority data={data} />;
    case "who_this_is_for":
      return <PreviewBulletSection data={data} variant="success" dataSectionName="who_for" />;
    case "who_this_is_not_for":
      return <PreviewBulletSection data={data} variant="danger" dataSectionName="who_not_for" />;
    case "mid_cta":
      return <PreviewMidCta data={data} />;
    case "what_happens_next":
      return <PreviewWhatHappensNext data={data} />;
    case "what_this_is":
      return <PreviewWhatComposite isData={data} notData={allSectionData["what_this_is_not"]} />;
    case "what_this_is_not":
      return null;  // Consumed by what_this_is composite — mirrors runtime render_what_this_is_not()
    case "faq":
      return <PreviewFAQ data={data} />;
    case "final_cta":
      return <PreviewFinalCta data={data} />;
    case "funnel_embed":
      return <PreviewFunnelEmbed data={data} />;
    case "seo":
      return null;
    default:
      return null;
  }
}

export function hasPreviewContent(sectionKey, data) {
  if (sectionKey === "seo") return false;
  if (sectionKey === "what_this_is_not") return false;  // Consumed by what_this_is composite
  if (!data) return false;
  if (Array.isArray(data)) {
    return data.some((item) => {
      if (typeof item === "string") return item.trim() !== "";
      if (typeof item === "object" && item !== null) {
        return Object.values(item).some((v) => typeof v === "string" && v.trim() !== "");
      }
      return false;
    });
  }
  if (typeof data === "object") {
    return Object.entries(data).some(([, val]) => {
      if (typeof val === "string") return val.trim() !== "";
      if (Array.isArray(val)) return val.length > 0;
      return false;
    });
  }
  return false;
}

// Surface grouping — MA-LP-LAYOUT-027E: Delegates to canonical policy via resolveLPBrandTokens
// Reads from sectionSurfacePolicy.json (single source of truth)
export function getSurfaceConfig() {
  return _getSurfaceConfig(_T);
}

// Backward compat: export SURFACE_CONFIG as the default config (static)
// MA-LP-THEME-036: Uses DEFAULT_LP_THEME as single canonical default
export const SURFACE_CONFIG = _getSurfaceConfig(resolveLPBrandTokens(DEFAULT_LP_THEME));
